import Bounds from '../utils/Bounds';
import { TextureSet, UVs, SpriteTexture } from './Texture';
import DynamicTexture from './DynamicTexture';

export class SDFCharacter {
	size: number;
	space: number;
	uvs: UVs;
	textureBounds: Bounds;
	dimensions: Bounds;

	constructor (size: number, space: number, spriteTexture: SpriteTexture, dimensions: Bounds) {
		this.size = size;
		this.space = space;
		this.uvs = spriteTexture.coordinates;
		this.textureBounds = spriteTexture.bounds;
		this.dimensions = dimensions;
	}
}

const canvas = document.createElement('canvas');
canvas.width = 1;
canvas.height = 1;

const contextAttempt = canvas.getContext('2d');
if (contextAttempt === null) {
	throw new Error('BitmapText: Could not create context 2D');
}

const context: CanvasRenderingContext2D = contextAttempt;
context.fillStyle = '#000000';

// arrays for SDF computation
let gridOuter = new Float64Array(0);
let gridInner = new Float64Array(0);
let f = new Float64Array(0);
let z = new Float64Array(0);
let v = new Uint16Array(0);

const INF = 1e20;
const sdfRadius = 0.25;

class SDFTexture {
	characterSize: number;
	characters: { [character: string]: SDFCharacter };
	dynamicTexture: DynamicTexture;
	spriteTexture: SpriteTexture;
	emptyTexture: SpriteTexture;
	textureSet: TextureSet;

	constructor (characterSize) {
		this.characterSize = characterSize;
		this.dynamicTexture = new DynamicTexture(1);
		this.characters = {};

		const texture = this.dynamicTexture.texture;
		this.spriteTexture = new SpriteTexture(this.dynamicTexture.texture, new Bounds(0, texture.width, 0, texture.height));
		this.emptyTexture = new SpriteTexture(this.dynamicTexture.texture, new Bounds(0, 0, 0, 0));
		this.textureSet = new TextureSet([this.spriteTexture], 1);
	}
}

export default class SDFFont {
	font: string;
	sdfTextures: SDFTexture[];

	constructor (font: string) {
		this.font = font;
		this.sdfTextures = [];
	}

	private _getMinimumSDFSize (characterSize) {
		// This is a good heuristic to determine proper rendering size based on desired character size
		return Math.ceil(characterSize / Math.sqrt(characterSize / 64));
	}

	getTexture (characterSize: number): SDFTexture | null {
		const sdfTextures = this.sdfTextures;
		const minimumSDFSize = this._getMinimumSDFSize(characterSize * 0.8);
		for (let i = sdfTextures.length - 1; i >= 0; i--) {
			const sdfTexture = sdfTextures[i];
			const sdfCharacterSize = sdfTexture.characterSize;
			if (sdfCharacterSize > minimumSDFSize) {
				return sdfTexture;
			}
		}

		return null;
	}

	updateWithCharacters (characters: string, characterSize: number) {
		const minimumSDFSize = this._getMinimumSDFSize(characterSize * 1.0);

		// Figuring out best size to render the characters
		// Finding character size that requires the minimum amount of work to prerender the given text
		let minRenderSurface = characters.length * minimumSDFSize * minimumSDFSize;
		let minRenderSDFTexture;

		const sdfTextures = this.sdfTextures;
		for (let i = 0; i < sdfTextures.length; i++) {
			const sdfTexture = sdfTextures[i];
			const sdfCharacters = sdfTexture.characters;
			const sdfCharacterSize = sdfTexture.characterSize;
			if (sdfCharacterSize < minimumSDFSize) {
				break;
			}

			let renderSurface = 0;
			for (let c = 0; c < characters.length; c++) {
				const character = sdfCharacters[characters[c]];
				if (character === undefined) {
					renderSurface += sdfCharacterSize * sdfCharacterSize;
				}
			}

			if (minRenderSurface >= renderSurface) {
				minRenderSurface = renderSurface;
				minRenderSDFTexture = sdfTexture;
			}
		}

		if (minRenderSDFTexture === undefined) {
			const upscale = 1.5;
			const sdfCharSize = this._getMinimumSDFSize(upscale * characterSize);
			minRenderSDFTexture = new SDFTexture(sdfCharSize);
			this.sdfTextures.push(minRenderSDFTexture);
			this.sdfTextures.sort(function (a, b) {
				return b.characterSize - a.characterSize;
			});
		} else if (minRenderSurface === 0) {
			return minRenderSDFTexture;
		}

		return this._updateWithCharacters(minRenderSDFTexture, characters);
	}

	_updateWithCharacters (sdfTexture: SDFTexture, characters: string) {
		// console.time('updateWithCharacters');

		const dynamicTexture = sdfTexture.dynamicTexture;
		const sdfCharacters = sdfTexture.characters;
		const sdfCharSize = sdfTexture.characterSize;

		context.font = sdfCharSize + 'px ' + this.font;
		for (let c = 0; c < characters.length; c++) {
			const stringCharacter = characters[c];
			if (sdfCharacters[stringCharacter] !== undefined) {
				continue;
			}

			const dimensions = context.measureText(stringCharacter);
			let charSpace = dimensions.width;
			let left, right, top, bottom;
			// actualBoundingBoxLeft API not supported by every browser
			// when unsupported there is a bit of wasted space on the texture
			if (dimensions.actualBoundingBoxLeft === undefined) {
				left = 0;
				right = charSpace;
				top = sdfCharSize;
				bottom = 0;
			} else {
				left = -dimensions.actualBoundingBoxLeft;
				right = dimensions.actualBoundingBoxRight;
				top = dimensions.actualBoundingBoxAscent;
				bottom = -dimensions.actualBoundingBoxDescent;
			}

			const width = right - left;
			const height = top - bottom;

			let characterTexture;
			if (width <= 0 || height <= 0) {
				characterTexture = this.sdfTextures[0].emptyTexture;
				left = 0;
				right = sdfCharSize / 4;
				top = 1;
				bottom = 0;
			} else {
				// const char = this.renderCharacter(stringCharacter, sdfCharSize, left, top, width, height);
				const characterImage = this.renderCharacterSDF(stringCharacter, sdfCharSize, left, top, width, height);
				characterTexture = dynamicTexture.addImage(characterImage);
	
				// Without the adjustment characters of different sizes would not be properly aligned
				const adjustX = 2 * (characterImage.width - 0.5 * (width + sdfCharSize * sdfRadius * 2));
				left -= adjustX;
				right -= adjustX;
	
				const adjustY = 2 * (characterImage.height - 0.5 * (height + sdfCharSize * sdfRadius * 2));
				bottom -= adjustY;
				top -= adjustY;
			}

			sdfCharacters[stringCharacter] = new SDFCharacter(sdfCharSize, charSpace, characterTexture, new Bounds(left, right, bottom, top));
		}

		// console.timeEnd('updateWithCharacters');
		return sdfTexture;
	}

	// private renderCharacter (character: string, size: number, left: number, top: number, width: number, height: number) {
	// 	const renderCanvas = document.createElement('canvas');
	// 	const renderContext = renderCanvas.getContext('2d');
	// 	if (renderContext === null) {
	// 		throw new Error('Could not create 2d context for character:' + character);
	// 	}
	// 	renderCanvas.width = width;
	// 	renderCanvas.height = height;

	// 	renderContext.font = size + 'px ' + this.font;
	// 	renderContext.fillStyle = '#000000';
	// 	renderContext.fillText(character, -left, top);

	// 	renderCanvas.style.position = 'absolute';
	// 	renderCanvas.style.top = (top / 2) + 'px';
	// 	renderCanvas.style.left = (left / 2) + 'px';
	// 	renderCanvas.style.width = (renderCanvas.width / 2) + 'px';
	// 	renderCanvas.style.height = (renderCanvas.height / 2) + 'px';
	// 	document.body.appendChild(renderCanvas);

	// 	return renderCanvas;
	// }

	private edt (data, width, height, f, v, z) {
		for (let x = 0; x < width; x++) {
			this.edt1d(data, x, width, height, f, v, z);
		}

		for (let y = 0; y < height; y++) {
			this.edt1d(data, y * width, 1, width, f, v, z);
		}
	}

	private edt1d (grid, offset, stride, length, f, v, z) {
		v[0] = 0;
		z[0] = -INF;
		z[1] = INF;

		for (let q = 0; q < length; q++) {
			f[q] = grid[offset + q * stride];
		}

		for (let q = 1, k = 0, s = 0; q < length; q++) {
			do {
					let r = v[k];
					s = (f[q] - f[r] + q * q - r * r) / (q - r) / 2;
			} while (s <= z[k] && --k > -1);

			k++;
			v[k] = q;
			z[k] = s;
			z[k + 1] = INF;
		}

		for (let q = 0, k = 0; q < length; q++) {
			while (z[k + 1] < q) {
				k++;
			}
			let r = v[k];
			grid[offset + q * stride] = f[r] + (q - r) * (q - r);
		}
	}

	private renderCharacterSDF (character: string, size: number, left: number, top: number, width: number, height: number) {
		const radius = size * sdfRadius;

		width = Math.round(width / 2 + radius) * 2;
		height = Math.round(height / 2 + radius) * 2;

		const canvasWidth = width;
		const canvasHeight = height;
		if (canvas.width < canvasWidth || canvas.height < canvasHeight) {
			canvas.width = canvasWidth;
			canvas.height = canvasHeight;
			context.font = size + 'px ' + this.font;
			context.fillStyle = '#000000';
		} else {
			context.clearRect(0, 0, canvasWidth, canvasHeight)
		}

		context.fillText(character, -left + radius, top + radius);

		const source = context.getImageData(0, 0, width, height);
		const sourceData = source.data;

		const resolution = width * height;
		if (resolution > gridOuter.length) {
			gridOuter = new Float64Array(resolution);
			gridInner = new Float64Array(resolution);
		}
		// const gridOuter = new Float64Array(resolution);
		// const gridInner = new Float64Array(resolution);

		for (let i = 0; i < resolution; i++) {
			const a = sourceData[i * 4 + 3] / 255; // alpha value
			const outer = (a === 1) ? 0 : ((a === 0) ? INF : Math.max(0, 0.5 - a));
			const inner = (a === 1) ? INF : ((a === 0) ? 0 : Math.max(0, a - 0.5));
			gridOuter[i] = outer;
			gridInner[i] = inner;
		}

		const maxDim = Math.max(width, height)
		if (maxDim > f.length) {
			f = new Float64Array(maxDim);
			z = new Float64Array(maxDim + 1);
			v = new Uint16Array(maxDim);
		}
		// const f = new Float64Array(maxDim);
		// const z = new Float64Array(maxDim + 1);
		// const v = new Uint16Array(maxDim);

		this.edt(gridOuter, width, height, f, v, z);
		this.edt(gridInner, width, height, f, v, z);

		const downSampling = 2;
		const destWidth = width / downSampling;
		const destHeight = height / downSampling;
		let destination = new ImageData(destWidth, destHeight);
		let sdf = destination.data;
		const destResolution = destWidth * destHeight;
		for (let i = 0; i < destResolution; i++) {
			const i_ = Math.floor(i / destWidth);
			const j1 = (i_ * width + (i - i_ * destWidth)) * width / destWidth;
			const j2 = j1 + 1;
			const j3 = j1 + width;
			const j4 = j1 + width + 1;
			const outer = Math.sqrt((gridOuter[j1] + gridOuter[j2] + gridOuter[j3] + gridOuter[j4]) / 4);
			const inner = Math.sqrt((gridInner[j1] + gridInner[j2] + gridInner[j3] + gridInner[j4]) / 4);
			sdf[4 * i + 3] = Math.round(128 - 128 * (outer - inner) / radius);
		}

		// return source;
		return destination;
	}
}

const sdfFonts: { [fontID: string]: SDFFont; } = {};

export function getSDFFont (fontID: string, createIfMissing: boolean = false) {
	let sdfFont = sdfFonts[fontID];
	if (sdfFont === undefined && createIfMissing) {
		sdfFont = sdfFonts[fontID] = new SDFFont(fontID);
	}

	return sdfFont;
}

// var canvas = document.createElement('canvas');

// var canvasDim = 2048;
// canvas.style.position = 'absolute';
// canvas.style.top = '0px';
// canvas.style.left = '0px';
// canvas.style.width = (canvasDim / 2) + 'px';
// canvas.style.height = (canvasDim / 2) + 'px';
// canvas.width = canvasDim;
// canvas.height = canvasDim;
// document.body.appendChild(canvas);

// var context = canvas.getContext('2d');
// if (context === null) {
// 	throw new Error('Could not create 2d context');
// }

// context.font = '64px arial';
// context.fillStyle = '#000000';
// context.fillText('The lazy dog jump over the brown fox', 50, 50)
